import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { Plus, Pencil, Trash2, Eye, EyeOff, Calendar, X, Upload, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';
import { API_BASE } from '../../config/api';

const API = API_BASE;
const EMPTY_EVENT = { title: '', date: '', time: '', location: '', description: '', image: '', cta_text: 'Learn More', external_url: '', category: 'leadership', status: 'upcoming', visible: true };

export default function AdminEventsPage() {
  const { api } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_EVENT);
  const [saving, setSaving] = useState(false);

  const fetchEvents = useCallback(() => {
    api.get('/admin/events').then(({ data }) => setEvents(data.items)).finally(() => setLoading(false));
  }, [api]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const openNew = () => { setForm(EMPTY_EVENT); setEditing('new'); };
  const openEdit = (ev) => { setForm({ title: ev.title, date: ev.date, time: ev.time || '', location: ev.location || '', description: ev.description || '', image: ev.image || '', cta_text: ev.cta_text || 'Learn More', external_url: ev.external_url || '', category: ev.category || 'leadership', status: ev.status || 'upcoming', visible: ev.visible !== false }); setEditing(ev.id); };
  const close = () => setEditing(null);

  const save = async () => {
    setSaving(true);
    if (editing === 'new') {
      await api.post('/admin/events', form);
    } else {
      await api.put(`/admin/events/${editing}`, form);
    }
    setSaving(false);
    close();
    setLoading(true);
    fetchEvents();
  };

  const toggleVisibility = async (ev) => {
    await api.put(`/admin/events/${ev.id}`, { visible: !ev.visible });
    fetchEvents();
  };

  const deleteEvent = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    await api.delete(`/admin/events/${id}`);
    fetchEvents();
  };

  const setField = (key, val) => setForm(f => ({ ...f, [key]: val }));

  return (
    <AdminLayout>
      <div data-testid="admin-events">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-medium" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Events</h1>
            <p className="text-sm text-gray-500">{events.length} events</p>
          </div>
          <button onClick={openNew} className="flex items-center gap-2 px-4 py-2.5 text-white text-xs font-medium rounded-lg transition-colors" style={{ background: 'linear-gradient(135deg, #E84993, #B565CC)' }} data-testid="add-event-btn">
            <Plus className="w-4 h-4" /> Add Event
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-[#E84993] border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <table className="w-full text-sm" data-testid="events-table">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-gray-400 font-medium">Event</th>
                  <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-gray-400 font-medium">Date</th>
                  <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-gray-400 font-medium">Category</th>
                  <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-gray-400 font-medium">Status</th>
                  <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-gray-400 font-medium">Visible</th>
                  <th className="text-right px-5 py-3 text-xs uppercase tracking-wider text-gray-400 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {events.map(ev => (
                  <tr key={ev.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-5 py-3.5 font-medium text-gray-900 max-w-[200px] truncate">{ev.title}</td>
                    <td className="px-5 py-3.5 text-gray-500">{ev.date}</td>
                    <td className="px-5 py-3.5"><span className="text-xs bg-[#FFE8F0] text-[#D13A7A] px-2.5 py-1 rounded-full capitalize">{(ev.category || '').replace('_', ' ')}</span></td>
                    <td className="px-5 py-3.5"><span className={`text-xs px-2.5 py-1 rounded-full capitalize ${ev.status === 'upcoming' ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-500'}`}>{ev.status}</span></td>
                    <td className="px-5 py-3.5">
                      <button onClick={() => toggleVisibility(ev)} className="text-gray-400 hover:text-[#E84993] transition-colors" data-testid={`toggle-visible-${ev.id}`}>
                        {ev.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      <div className="flex items-center justify-end gap-3">
                        <button onClick={() => openEdit(ev)} className="text-gray-400 hover:text-[#E84993] transition-colors" data-testid={`edit-event-${ev.id}`}><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => deleteEvent(ev.id)} className="text-gray-400 hover:text-red-500 transition-colors" data-testid={`delete-event-${ev.id}`}><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Modal */}
        {editing !== null && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={close}>
            <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="event-form-modal">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <h3 className="font-medium">{editing === 'new' ? 'Add Event' : 'Edit Event'}</h3>
                <button onClick={close}><X className="w-5 h-5 text-gray-400" /></button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Title *</label>
                  <input value={form.title} onChange={e => setField('title', e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#E84993]" data-testid="event-title-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Date *</label>
                    <input type="date" value={form.date} onChange={e => setField('date', e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#E84993]" data-testid="event-date-input" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Time</label>
                    <input value={form.time} onChange={e => setField('time', e.target.value)} placeholder="e.g. 9:00 AM - 5:00 PM" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#E84993]" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Location</label>
                  <input value={form.location} onChange={e => setField('location', e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#E84993]" />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Description</label>
                  <textarea value={form.description} onChange={e => setField('description', e.target.value)} rows={3} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#E84993] resize-none" />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Event Image</label>
                  <ImageUpload value={form.image} onChange={(url) => setField('image', url)} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Category</label>
                    <select value={form.category} onChange={e => setField('category', e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#E84993]">
                      <option value="leadership">Leadership</option>
                      <option value="personal_growth">Personal Growth</option>
                      <option value="business">Business</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Status</label>
                    <select value={form.status} onChange={e => setField('status', e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#E84993]">
                      <option value="upcoming">Upcoming</option>
                      <option value="past">Past</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">CTA Text</label>
                  <input value={form.cta_text} onChange={e => setField('cta_text', e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#E84993]" />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">External URL</label>
                  <input value={form.external_url} onChange={e => setField('external_url', e.target.value)} placeholder="https://..." className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#E84993]" />
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={form.visible} onChange={e => setField('visible', e.target.checked)} id="vis" className="rounded" />
                  <label htmlFor="vis" className="text-sm text-gray-600">Visible on public site</label>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
                <button onClick={close} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">Cancel</button>
                <button onClick={save} disabled={saving || !form.title || !form.date} className="px-6 py-2 text-white text-sm font-medium rounded-lg disabled:opacity-40 transition-colors" style={{ background: 'linear-gradient(135deg, #E84993, #B565CC)' }} data-testid="save-event-btn">
                  {saving ? 'Saving...' : editing === 'new' ? 'Create Event' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}


/* Image upload component */
function ImageUpload({ value, onChange }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await axios.post(`${API}/api/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange(data.url);
    } catch (err) {
      alert('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      {value && (
        <div className="mb-2 relative inline-block">
          <img src={value} alt="Preview" className="w-full h-32 object-cover rounded-lg border border-gray-200" />
          <button onClick={() => onChange('')} className="absolute top-1 right-1 w-5 h-5 bg-white rounded-full flex items-center justify-center shadow text-gray-400 hover:text-red-500">
            <X className="w-3 h-3" />
          </button>
        </div>
      )}
      <input type="file" ref={fileRef} onChange={handleUpload} accept="image/*" className="hidden" />
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-200 rounded-lg text-sm text-gray-500 hover:border-[#E84993] hover:text-[#E84993] transition-colors disabled:opacity-50"
        data-testid="image-upload-btn"
      >
        {uploading ? (
          <><div className="w-4 h-4 border-2 border-[#E84993] border-t-transparent rounded-full animate-spin" /> Uploading...</>
        ) : (
          <><Upload className="w-4 h-4" /> {value ? 'Change Image' : 'Upload Image'}</>
        )}
      </button>
    </div>
  );
}
